import { apiFetch } from "@/lib/api-client";
import type {
  CategoryCount,
  InboxNotification,
  InboxPage,
  InboxQuery,
  ReadAllInput,
} from "@/lib/autar/notifications";

function isNotification(value: unknown): value is InboxNotification {
  const row = value as Partial<InboxNotification> | null;
  return (
    typeof row === "object" &&
    row !== null &&
    typeof row.id === "string" &&
    typeof row.type === "string" &&
    typeof row.category === "string" &&
    typeof row.createdAt === "string" &&
    typeof row.data === "object" &&
    row.data !== null
  );
}

const path = (id: string, action = "") =>
  `/notifications/${encodeURIComponent(id)}${action}`;

export async function listNotifications(
  query: InboxQuery = {},
  signal?: AbortSignal,
): Promise<InboxPage> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const qs = params.toString();
  const page = await apiFetch<InboxPage>(
    `/notifications${qs ? `?${qs}` : ""}`,
    { signal },
  );
  if (
    !page ||
    !Array.isArray(page.items) ||
    !page.items.every(isNotification) ||
    (page.nextCursor !== null && typeof page.nextCursor !== "string")
  )
    throw new Error("Invalid notification list response");
  return page;
}

export async function getUnreadCount(signal?: AbortSignal): Promise<number> {
  const result = await apiFetch<{ count: number }>(
    "/notifications/unread-count",
    { signal },
  );
  if (!Number.isInteger(result?.count) || result.count < 0)
    throw new Error("Invalid unread count response");
  return result.count;
}

export async function getCategoryCounts(
  signal?: AbortSignal,
): Promise<CategoryCount[]> {
  const result = await apiFetch<{ categories: CategoryCount[] }>(
    "/notifications/categories",
    { signal },
  );
  if (
    !Array.isArray(result?.categories) ||
    !result.categories.every(
      (c) =>
        typeof c?.category === "string" &&
        Number.isInteger(c.total) &&
        Number.isInteger(c.unread),
    )
  )
    throw new Error("Invalid category counts response");
  return result.categories;
}

export const markNotificationRead = (id: string) =>
  apiFetch<{ id: string; read: boolean }>(path(id, "/read"), {
    method: "POST",
  });

export const markNotificationUnread = (id: string) =>
  apiFetch<{ id: string; read: boolean }>(path(id, "/unread"), {
    method: "POST",
  });

export const markAllNotificationsRead = (input: ReadAllInput) =>
  apiFetch<{ updated: number }>("/notifications/read-all", {
    method: "POST",
    body: JSON.stringify(input),
  });

export const deleteNotification = (id: string) =>
  apiFetch<void>(path(id), { method: "DELETE" });
